import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { getUserCases, getCaseStats } from '../services/caseService';
import { AlertCircle, CheckCircle, Clock, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';

const CaseManagement = () => {
  const { currentUser, currentOrg } = useAuth();
  const t = useTranslation();
  const mountedRef = useRef(true);
  const hasFetchedRef = useRef(false);
  
  // State for cases
  const [cases, setCases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all-open');
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    completed: 0
  });

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    console.log("CaseManagement Mounted");
    return () => {
      mountedRef.current = false;
      console.log("CaseManagement Unmounted");
    };
  }, []);
  
  // Memoize the fetch function
  const fetchCases = useCallback(async () => {
    if (!currentUser || !currentOrg || !mountedRef.current || hasFetchedRef.current) {
      console.log("Skipping fetch: Conditions not met or already fetched.", { userId: currentUser?.uid, orgId: currentOrg?.id, mounted: mountedRef.current, fetched: hasFetchedRef.current });
      if (hasFetchedRef.current && isLoading) {
         if (mountedRef.current) setIsLoading(false);
      }
      return;
    }
    
    console.log("Fetching cases started...");
    if (mountedRef.current) setIsLoading(true);
    hasFetchedRef.current = true; 
    
    try {
      console.log("Fetching cases for organization:", currentOrg.id);
      
      // Get case statistics
      const statsData = await getCaseStats(currentOrg.id);
      
      // Get all cases for the organization
      const userCases = await getUserCases(currentOrg.id);
      console.log(`Got ${userCases.length} cases from the cases collection`);
      
      if (mountedRef.current) {
        console.log("Updating state with:", { 
          casesCount: userCases.length, 
          stats: statsData,
          firstCase: userCases[0]?.timestamp?.toISOString?.() || 'no cases'
        });
        setCases(userCases);
        setStats(statsData);
        setError(null);
        setIsLoading(false);
        console.log("State update complete. isLoading is now false.");
      } else {
        console.log("Component unmounted before state update.");
      }

    } catch (error) {
      console.error('Error fetching cases:', error);
      if (mountedRef.current) {
        // Use static string temporarily to rule out t issues
        setError('Error loading cases. Please try again.'); 
        setCases([]); // Clear potentially stale data on error
        setStats({ total: 0, pending: 0, inProgress: 0, completed: 0 });
        setIsLoading(false); // Set loading false LAST, even on error
        console.log("Error occurred during fetch. isLoading set to false.");
      }
    }
  }, [currentUser, currentOrg]);

  useEffect(() => {
    console.log("Fetch effect triggered. User ID:", currentUser?.uid, "Org ID:", currentOrg?.id);
    if (currentUser && currentOrg) {
      hasFetchedRef.current = false; 
      console.log("User found, calling fetchCases.");
      fetchCases();
    } else {
      console.log("No user or organization found, clearing state and stopping load.");
      if (mountedRef.current) {
          setCases([]);
          setStats({ total: 0, pending: 0, inProgress: 0, completed: 0 });
          setError(null);
          setIsLoading(false);
      }
      hasFetchedRef.current = false;
    }
  }, [currentUser, currentOrg, fetchCases]);

  const refreshCases = useCallback(() => {
    console.log("Manual refresh triggered.");
    hasFetchedRef.current = false;
    fetchCases();
  }, [fetchCases]);
  
  const getFilteredCases = () => {
    return cases.filter(caseItem => {
      if (activeFilter === 'all-open') {
        return caseItem.status === 'pending' || caseItem.status === 'partially-reviewed';
      } else if (activeFilter === 'pending') {
        return caseItem.status === 'pending';
      } else if (activeFilter === 'in-progress') {
        return caseItem.status === 'partially-reviewed';
      } else if (activeFilter === 'completed') {
        return caseItem.status === 'completed';
      }
      return true;
    });
  };
  
  const formatRelativeTime = (timestamp) => {
    try {
      // Ensure we have a valid Date object
      if (!timestamp || !(timestamp instanceof Date) || isNaN(timestamp.getTime())) {
        console.warn('Invalid timestamp passed to formatRelativeTime:', timestamp);
        return 'Unknown time';
      }
      
      const now = new Date();
      const diffMs = now.getTime() - timestamp.getTime();
      
      // Log the calculation details for debugging
      console.log('Time calculation:', {
        now: now.toISOString(),
        timestamp: timestamp.toISOString(),
        diffMs: diffMs,
        diffMins: Math.floor(diffMs / (1000 * 60)),
        diffHours: Math.floor(diffMs / (1000 * 60 * 60)),
        diffDays: Math.floor(diffMs / (1000 * 60 * 60 * 24)),
      });
      
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffMins < 0) return 'Future date';
      if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? t('cases.minute') : t('cases.minutes')} ${t('cases.ago')}`;
      if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? t('cases.hour') : t('cases.hours')} ${t('cases.ago')}`;
      if (diffDays < 30) return `${diffDays} ${diffDays === 1 ? t('cases.day') : t('cases.days')} ${t('cases.ago')}`;
      
      // For older dates use a more readable format
      return timestamp.toLocaleDateString(undefined, {
        year: 'numeric', 
        month: 'short', 
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting time:', error);
      return 'Unknown time';
    }
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={16} className="text-emerald-500" />;
      case 'partially-reviewed':
        return <Clock size={16} className="text-purple-500" />;
      default:
        return <AlertCircle size={16} className="text-amber-500" />;
    }
  };
  
  const getStatusClass = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-emerald-500';
      case 'partially-reviewed':
        return 'bg-purple-500';
      default:
        return 'bg-amber-500';
    }
  };
  
  const renderMatchCount = (count, type) => {
    if (!count) return null;
    
    let tooltipText = '';
    let bgColorClass = '';
    
    switch (type) {
      case 'very-high': 
        tooltipText = 'Very High Risk Matches'; 
        bgColorClass = 'bg-red-500';
        break;
      case 'high': 
        tooltipText = 'High Risk Matches'; 
        bgColorClass = 'bg-amber-500';
        break;
      case 'medium': 
        tooltipText = 'Medium Risk Matches'; 
        bgColorClass = 'bg-blue-500';
        break;
      case 'low': 
        tooltipText = 'Low Risk Matches'; 
        bgColorClass = 'bg-gray-500';
        break;
      default: 
        tooltipText = 'Matches';
        bgColorClass = 'bg-gray-500';
    }

    return (
      <span 
        className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold text-white ${bgColorClass}`} 
        title={`${count} ${tooltipText}`}
      >
        {count}
      </span>
    );
  };

  const filteredCases = getFilteredCases();
  
  console.log("Rendering CaseManagement component:", {
    isLoading,
    hasError: !!error,
    casesCount: cases.length,
    activeFilter,
    filteredCount: filteredCases.length
  });
  
  return (
    <div className="flex flex-col w-full bg-gray-100 dark:bg-slate-900 p-3 overflow-y-auto max-h-full">
      <div className="max-w-5xl mx-auto w-full">
        <div className="bg-white dark:bg-slate-800 rounded-lg p-4 mb-4 shadow-sm">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-50 m-0 mb-3">{t('cases.title')}</h2>
            <button 
              onClick={refreshCases} 
              className="flex items-center gap-1 px-3 py-1.5 rounded bg-transparent border border-blue-500 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 text-xs cursor-pointer"
            >
              <RefreshCw size={14} />
              {t('cases.refresh')}
            </button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            <div className="bg-gray-100 dark:bg-slate-700 rounded-md p-2.5 text-center transition-transform hover:-translate-y-0.5">
              <div className="text-2xl font-bold text-gray-800 dark:text-slate-50 mb-1">{stats.total}</div>
              <div className="text-xs text-gray-600 dark:text-slate-300">{t('cases.totalCases')}</div>
            </div>
            
            <div className="bg-red-100 dark:bg-red-900/80 rounded-md p-2.5 text-center transition-transform hover:-translate-y-0.5">
              <div className="text-2xl font-bold text-red-800 dark:text-white mb-1">{stats.pending + stats.inProgress}</div>
              <div className="text-xs text-red-700 dark:text-white/80">{t('cases.allOpenCases')}</div>
            </div>
            
            <div className="bg-purple-100 dark:bg-purple-900/80 rounded-md p-2.5 text-center transition-transform hover:-translate-y-0.5">
              <div className="text-2xl font-bold text-purple-800 dark:text-white mb-1">{stats.inProgress}</div>
              <div className="text-xs text-purple-700 dark:text-white/80">{t('cases.inProgressCases')}</div>
            </div>
            
            <div className="bg-emerald-100 dark:bg-emerald-900/80 rounded-md p-2.5 text-center transition-transform hover:-translate-y-0.5">
              <div className="text-2xl font-bold text-emerald-800 dark:text-white mb-1">{stats.completed}</div>
              <div className="text-xs text-emerald-700 dark:text-white/80">{t('cases.completedCases')}</div>
            </div>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2 mb-4">
          <button 
            className={`text-xs py-1.5 px-3 rounded-full border-none cursor-pointer transition-colors ${
              activeFilter === 'all-open' 
                ? 'bg-blue-600 text-white' 
                : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700'
            }`}
            onClick={() => setActiveFilter('all-open')}
          >
            {t('cases.filters.allOpen')} ({stats.pending + stats.inProgress})
          </button>
          
          <button 
            className={`text-xs py-1.5 px-3 rounded-full border-none cursor-pointer transition-colors ${
              activeFilter === 'pending' 
                ? 'bg-blue-600 text-white' 
                : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700'
            }`}
            onClick={() => setActiveFilter('pending')}
          >
            {t('cases.filters.pending')} ({stats.pending})
          </button>
          
          <button 
            className={`text-xs py-1.5 px-3 rounded-full border-none cursor-pointer transition-colors ${
              activeFilter === 'in-progress' 
                ? 'bg-blue-600 text-white' 
                : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700'
            }`}
            onClick={() => setActiveFilter('in-progress')}
          >
            {t('cases.filters.inProgress')} ({stats.inProgress})
          </button>
          
          <button 
            className={`text-xs py-1.5 px-3 rounded-full border-none cursor-pointer transition-colors ${
              activeFilter === 'completed' 
                ? 'bg-blue-600 text-white' 
                : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700'
            }`}
            onClick={() => setActiveFilter('completed')}
          >
            {t('cases.filters.completed')} ({stats.completed})
          </button>
        </div>
        
        <div className="w-full">
          {isLoading ? (
            <div className="text-center py-5 bg-white dark:bg-slate-800 rounded-lg text-gray-600 dark:text-slate-300 text-sm animate-pulse">
              {t('cases.loading')}
            </div>
          ) : error ? (
            <div className="text-center py-5 bg-white dark:bg-slate-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          ) : filteredCases.length === 0 ? (
            <div className="text-center py-5 bg-white dark:bg-slate-800 rounded-lg text-gray-600 dark:text-slate-300 text-sm">
              {t('cases.noCases')}
            </div>
          ) : (
            <div className="flex flex-col gap-2.5">
              {filteredCases.map(caseItem => {
                console.log(`Case item ${caseItem.id} verification:`, {
                  hasVerificationId: !!caseItem.verificationId,
                  verificationId: caseItem.verificationId
                });
                
                return (
                  <Link 
                    to={`/verification/${caseItem.verificationId}`}
                    state={{ from: 'cases' }}
                    key={caseItem.id} 
                    className="relative flex flex-col bg-white dark:bg-slate-800 rounded-lg p-3 no-underline transition-all hover:-translate-y-0.5 hover:shadow-md border border-gray-200 dark:border-slate-700/20 hover:border-gray-300 dark:hover:border-slate-600/30 overflow-hidden"
                  >
                    <div className={`absolute left-0 top-0 bottom-0 w-1 ${getStatusClass(caseItem.status)}`}></div>
                    <div className="flex justify-between items-center mb-2.5 ml-2.5">
                      <div className="font-semibold text-[15px] text-gray-900 dark:text-slate-50 break-words">
                        {caseItem.searchTerm}
                      </div>
                      <div className="flex items-center text-xs px-2 py-0.5 rounded-xl bg-gray-100 dark:bg-slate-900 text-gray-600 dark:text-slate-300 border border-gray-200 dark:border-slate-700">
                        {getStatusIcon(caseItem.status)}
                        <span className="ml-1.5 text-xs text-gray-600 dark:text-slate-300">
                          {caseItem.status === 'completed' ? t('cases.statusCompleted') :
                           caseItem.status === 'partially-reviewed' ? t('cases.statusInProgress') :
                           t('cases.statusPending')}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2 ml-2.5">
                      <div className="flex items-center text-xs text-gray-600 dark:text-slate-300">
                        <span className="mr-1">{t('cases.matches')}:</span>
                        <span className="font-semibold mr-2">{caseItem.totalMatches}</span>
                        
                        <div className="flex gap-1">
                          {renderMatchCount(caseItem.matchCounts?.veryHigh, 'very-high')}
                          {renderMatchCount(caseItem.matchCounts?.high, 'high')}
                          {renderMatchCount(caseItem.matchCounts?.medium, 'medium')}
                          {renderMatchCount(caseItem.matchCounts?.low, 'low')}
                        </div>
                      </div>
                      
                      <div className="mt-1">
                        <div className="h-1 w-full bg-gray-200 dark:bg-slate-900 rounded-sm overflow-hidden mb-1">
                          <div 
                            className="h-full bg-emerald-500 dark:bg-emerald-500 rounded-sm" 
                            style={{ 
                              width: `${Math.round(
                                ((caseItem.reviewCounts?.confirmed + caseItem.reviewCounts?.dismissed) / 
                                Math.max(caseItem.totalMatches, 1)) * 100
                              )}%` 
                            }}
                          ></div>
                        </div>
                        <div className="text-[11px] text-gray-600 dark:text-slate-300">
                          {caseItem.reviewCounts?.confirmed + caseItem.reviewCounts?.dismissed} / {caseItem.totalMatches} {t('cases.reviewed')}
                        </div>
                      </div>
                      
                      <div className="text-[11px] text-gray-500 dark:text-slate-400 mt-1.5 text-right">
                        <span>{formatRelativeTime(caseItem.timestamp)}</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CaseManagement; 